import { useState, useEffect } from "react";
import AddressAutocomplete from "./AddressAutocomplete";

export default function ClaimModal({ item, onPickup, onDelivery, onClose, homeLocation, prefilledDelivery, onRequestMapPick }) {
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryLat, setDeliveryLat] = useState(null);
  const [deliveryLng, setDeliveryLng] = useState(null);
  const [claimQty, setClaimQty] = useState(1);

  // Pre-fill with home location if available
  useEffect(() => {
    if (homeLocation?.address) {
      setDeliveryAddress(homeLocation.address);
      setDeliveryLat(homeLocation.lat);
      setDeliveryLng(homeLocation.lng);
    }
  }, [homeLocation]);

  // Reset when modal closes/opens
  useEffect(() => {
    if (!item) {
      setShowDeliveryForm(false);
      setClaimQty(1);
      if (homeLocation?.address) {
        setDeliveryAddress(homeLocation.address);
        setDeliveryLat(homeLocation.lat);
        setDeliveryLng(homeLocation.lng);
      } else {
        setDeliveryAddress("");
        setDeliveryLat(null);
        setDeliveryLng(null);
      }
    } else {
      // Default to claiming all available quantity
      const qty = parseInt(item.quantity, 10) || 1;
      setClaimQty(qty);
    }
  }, [item, homeLocation]);

  // Apply prefilled delivery from map pick
  useEffect(() => {
    if (prefilledDelivery && item) {
      setShowDeliveryForm(true);
      setDeliveryAddress(prefilledDelivery.address);
      setDeliveryLat(prefilledDelivery.lat);
      setDeliveryLng(prefilledDelivery.lng);
      if (prefilledDelivery.claimQty != null) setClaimQty(prefilledDelivery.claimQty);
    }
  }, [prefilledDelivery, item]);

  if (!item) return null;

  const maxQty = parseInt(item.quantity, 10) || 1;
  const showQtySelector = maxQty > 1;

  const handleDeliverySelect = ({ address, lat, lng }) => {
    setDeliveryAddress(address);
    setDeliveryLat(lat);
    setDeliveryLng(lng);
  };

  const handleConfirmDelivery = () => {
    if (!deliveryLat || !deliveryLng) {
      alert("Please select a delivery address from the suggestions.");
      return;
    }
    onDelivery(item.id, {
      deliveryAddress,
      deliveryLat,
      deliveryLng,
      claimQty
    });
  };

  const decrementQty = () => setClaimQty(q => Math.max(1, q - 1));
  const incrementQty = () => setClaimQty(q => Math.min(maxQty, q + 1));

  const handleSliderChange = (e) => {
    setClaimQty(parseInt(e.target.value, 10));
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 z-10"
        >
          ✕
        </button>

        {/* Food image */}
        <img
          src={item.imageUrl || "/image192.png"}
          alt={item.name}
          className="w-full h-36 object-cover rounded-2xl mb-4 border border-gray-200/60"
        />

        {/* Food details */}
        <div className="mb-4">
          <h2 className="font-condensed text-2xl font-bold text-gray-800 uppercase tracking-tight">{item.name}</h2>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span>Available: {maxQty}</span>
            <span>{item.hoursLeft}h left</span>
            {item.distance != null && <span>{item.distance.toFixed(1)} mi</span>}
          </div>
          {item.address && <p className="text-xs text-gray-400 mt-1 truncate">{item.address}</p>}
          {item.hoursLeft < 3 && (
            <span className="inline-block mt-2 text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Urgent</span>
          )}
        </div>

        {/* Quantity selector — only if qty > 1 */}
        {showQtySelector && (
          <div className="mb-5 p-4 bg-gray-50/80 rounded-2xl border border-gray-200/60">
            <p className="font-condensed text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">How many do you want?</p>

            {/* Stepper */}
            <div className="flex items-center justify-center gap-4 mb-3">
              <button
                onClick={decrementQty}
                disabled={claimQty <= 1}
                className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-lg font-bold text-gray-600 hover:bg-gray-50 transition disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
              >
                −
              </button>
              <div className="text-center min-w-[60px]">
                <span className="font-condensed text-3xl font-extrabold text-green-600">{claimQty}</span>
                <p className="text-[10px] text-gray-400 -mt-0.5">of {maxQty}</p>
              </div>
              <button
                onClick={incrementQty}
                disabled={claimQty >= maxQty}
                className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-lg font-bold text-gray-600 hover:bg-gray-50 transition disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
              >
                +
              </button>
            </div>

            {/* Slider for larger quantities */}
            {maxQty > 3 && (
              <div className="px-1">
                <input
                  type="range"
                  min={1}
                  max={maxQty}
                  value={claimQty}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>1</span>
                  <span>{maxQty}</span>
                </div>
              </div>
            )}

            {/* Quick select buttons for common amounts */}
            {maxQty > 2 && (
              <div className="flex gap-1.5 mt-3 justify-center">
                <button
                  onClick={() => setClaimQty(1)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${claimQty === 1 ? "bg-green-500 text-white" : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                >
                  Just 1
                </button>
                {maxQty >= 4 && (
                  <button
                    onClick={() => setClaimQty(Math.ceil(maxQty / 2))}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition ${claimQty === Math.ceil(maxQty / 2) ? "bg-green-500 text-white" : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                  >
                    Half ({Math.ceil(maxQty / 2)})
                  </button>
                )}
                <button
                  onClick={() => setClaimQty(maxQty)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${claimQty === maxQty ? "bg-green-500 text-white" : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                >
                  All ({maxQty})
                </button>
              </div>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-200/60 mb-5"></div>

        {!showDeliveryForm ? (
          <>
            <p className="font-condensed text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">How do you want to get it?</p>

            {/* Pickup button */}
            <button
              onClick={() => onPickup(item.id, claimQty)}
              className="btn-pop w-full flex items-center gap-4 p-4 mb-3 bg-green-50 border border-green-200 rounded-2xl hover:bg-green-100 group"
            >
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 group-hover:scale-110 transition">
                🚶
              </div>
              <div className="text-left">
                <p className="font-condensed text-base font-bold text-green-700 uppercase tracking-wide">Pickup</p>
                <p className="text-xs text-green-600/70">Go get {claimQty === maxQty ? "it" : `${claimQty}`} yourself</p>
              </div>
            </button>

            {/* Delivery button — opens address form */}
            <button
              onClick={() => setShowDeliveryForm(true)}
              className="btn-pop w-full flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-2xl hover:bg-blue-100 group"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 group-hover:scale-110 transition">
                🚴
              </div>
              <div className="text-left">
                <p className="font-condensed text-base font-bold text-blue-700 uppercase tracking-wide">Request Delivery</p>
                <p className="text-xs text-blue-600/70">A courier will bring {claimQty === maxQty ? "it" : `${claimQty}`} to you</p>
              </div>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowDeliveryForm(false)}
              className="text-xs text-gray-400 hover:text-gray-600 transition mb-3 flex items-center gap-1"
            >
              ← Back
            </button>

            <p className="font-condensed text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Where should it be delivered?</p>
            <p className="text-xs text-gray-400 mb-3">Enter the address or pick a spot on the map.</p>

            <AddressAutocomplete
              value={deliveryAddress}
              onChange={setDeliveryAddress}
              onSelect={handleDeliverySelect}
            />

            {onRequestMapPick && (
              <button
                type="button"
                onClick={() => onRequestMapPick(claimQty)}
                className="btn-pop mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-green-700 bg-green-50 border border-green-200 hover:bg-green-100"
              >
                <span>📍</span> Pick on map
              </button>
            )}

            {deliveryLat && deliveryLng && (
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Delivery address set
              </p>
            )}

            <button
              onClick={handleConfirmDelivery}
              className="btn-pop mt-4 w-full bg-blue-500 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-600 shadow-sm"
            >
              Confirm Delivery ({claimQty}{claimQty === 1 ? " item" : " items"})
            </button>
          </>
        )}
      </div>
    </div>
  );
}
